import { axiosInstance } from './axiosInstance'
import { getAccumulatedBom } from '../../../helpers/getAccumulatedBom'
import { type APIConfig } from '../apiTypes'
import type * as DynamakerContract from '../../../integration/DynamakerContract'

const axios = axiosInstance()

// TODO: Scensum can reject articles with HTTP 200, and the response body is never inspected
/*
  The response has never been observed, so the type is left as any. According to the
  backend it uses camelCase and, unlike dsapricehelper, always includes `errors`, empty
  when everything went well. The missing check is
  `errors.length > 0 || data.some((article) => !article.isAvailableForPurchase)`.
*/
type Response = any

interface BaseArticle {
  Sku: string
  Quantity: number
  ConfigurationType: string
  ConfigurationId: string
}

interface ConfigurableArticle extends BaseArticle {
  Type: 'configProduct'
  ContextId: string
  ChosenValues: number[]
  ConfigurationId: string
  ConfigString: string
  OrderRowDescription: string
  PricingInfo: string
  WeightVolumeDimensions: {
    WeightInKg: number | null
    WidthInMeters: number | null
    DepthInMeters: number | null
    HeightInMeters: number | null
    FreightVolumeCategory: number | null
  } | null
  Editorial: {
    Images: {
      imageId: string
      imagePath: string | null
      type: string
      ordinal: number
    }[]
    Attributes: {
      attributeValue: string
      attributeName: string
    }[]
  }
}

type Article = BaseArticle | ConfigurableArticle

interface PostCartAddConfigurationSectionsData {
  ConfigurationType: string
  ConfigurationId: string
  Meta: {
    Title: string
    TitleLong: string
  }
  ConfigurationSections: {
    Meta: {
      Title: string
      TitleLong: string
    }
    Type: string
    Position: number
    OwnerPosition: number | null
    Articles: Article[]
  }[]
  // TODO 5625: remove ConfigurationBomV1 when ConfigurationSections is fully supported and used in production
  ConfigurationBomV1: Article[]
}

export const postCartAddConfigurationSections = async <T = any>(args: {
  apiConfigs: APIConfig
  data: {
    bom: DynamakerContract.BOM
    configId: string
    content: Record<PropertyKey, any>
    meta: DynamakerContract.Metadata
  }
  onSuccess: (response: { data: Response }) => T
}): Promise<T> => {
  const { onSuccess } = args
  const { scensumBaseUrl } = args.apiConfigs
  const { bom, configId, content, meta } = args.data

  const accumulatedBom = getAccumulatedBom(bom)

  const configurationSections: PostCartAddConfigurationSectionsData['ConfigurationSections'] = accumulatedBom.map(
    // ts any
    (section: any) => ({
      Type: section.type,
      Position: section.position,
      OwnerPosition: section.ownerPosition ?? null,
      Meta: {
        Title: content?.bom?.[section.type]?.title ?? '(intentionally left blank)',
        TitleLong: content?.bom?.[section.type]?.titleLong
          ? `${content?.bom?.[section.type]?.titleLong} ${meta.buildingArea.toString().replace('.', ',')} m²`
          : '(intentionally left blank)',
      },
      // TODO ts
      Articles: section.articles.map((item: any) => {
        const article: Article = {
          Sku: item.erpItemNumber,
          Quantity: item.quantity,
          ConfigurationType: 'mok',
          ConfigurationId: configId,
          ...('configData' in item && item.configData && parseConfigDataForCart(item.configData)),
        }
        return article
      }),
    })
  )

  return await axios<Response, any, PostCartAddConfigurationSectionsData>({
    method: 'post',
    url: `${scensumBaseUrl}/cart/addconfigurationsections`,
    data: {
      ConfigurationType: 'mok',
      ConfigurationId: configId,
      Meta: {
        Title: '(intentionally left blank)',
        TitleLong: '(intentionally left blank)',
      },
      ConfigurationSections: configurationSections,
      // TODO 5625: remove ConfigurationBomV1 when ConfigurationSections is fully supported and used in production
      ConfigurationBomV1: toConfigurationBomV1(configurationSections),
    },
  })
    .then((response) => onSuccess(response))
    .catch(async (error) => {
      console.error(error)
      throw new Error(JSON.stringify(error))
    })
}

interface ConfigData {
  contextId: string
  chosenValues: number[]
  configResult: {
    configProducts: Array<{
      configString: string
      orderRowDescription: string
      pricingInfo: any
      weightVolumeDimensions: any
      editorial: any
    }>
  }
}

const parseConfigDataForCart = (
  configData: ConfigData
): Omit<ConfigurableArticle, 'ConfigurationType' | 'Quantity' | 'Sku' | 'ConfigurationId'> => {
  const { contextId, chosenValues, configResult } = configData

  const {
    configString,
    orderRowDescription,
    pricingInfo,
    weightVolumeDimensions,
    editorial,
    // only works for single config product in configResult, equivalent to current implementation in lfc-frontend
  } = configResult.configProducts[0]

  return {
    Type: 'configProduct',
    ContextId: contextId,
    ChosenValues: chosenValues,
    ConfigString: configString,
    OrderRowDescription: orderRowDescription,
    PricingInfo: pricingInfo,
    // TODO--LFC make this easier...
    WeightVolumeDimensions: weightVolumeDimensions
      ? {
          WeightInKg:
            weightVolumeDimensions.weightInKg || weightVolumeDimensions.weightInKg === 0
              ? weightVolumeDimensions.weightInKg
              : null,
          WidthInMeters:
            weightVolumeDimensions.widthInMeters || weightVolumeDimensions.widthInMeters === 0
              ? weightVolumeDimensions.widthInMeters
              : null,
          DepthInMeters:
            weightVolumeDimensions.depthInMeters || weightVolumeDimensions.depthInMeters === 0
              ? weightVolumeDimensions.depthInMeters
              : null,
          HeightInMeters:
            weightVolumeDimensions.heightInMeters || weightVolumeDimensions.heightInMeters === 0
              ? weightVolumeDimensions.heightInMeters
              : null,
          FreightVolumeCategory: weightVolumeDimensions.freightVolumeCategory || null,
        }
      : null,
    Editorial: {
      Images: editorial.images,
      Attributes: editorial.attributes,
    },
  }
}

// TODO 5625: remove ConfigurationBomV1 when ConfigurationSections is fully supported and used in production
const toConfigurationBomV1 = (
  configurationSections: PostCartAddConfigurationSectionsData['ConfigurationSections']
): PostCartAddConfigurationSectionsData['ConfigurationBomV1'] => {
  const flatBOM: PostCartAddConfigurationSectionsData['ConfigurationBomV1'] = configurationSections.flatMap(
    (section) => section.Articles
  )

  return flatBOM
}
